import { NavigateFunction } from 'react-router-dom';

import { createColumnHelper } from '@tanstack/react-table';
import MainTableRowActions from '@components/MainTable/MainTableRowActions';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  IconArrowNarrowDown,
  IconPencil,
  IconTrash,
  IconArrowNarrowUp,
  IconCopy,
} from '@tabler/icons-react';
import { TableUpdateNameCell } from '@components/TableUpdateNameCell';
import { updateDocumentGeneratorTemplate } from '@api/document-generator-template';
import { StatusTag } from '@shared/components';
import AnimateButton from '@components/extended/AnimateButton';
import { routes } from '@shared/constants/paths';
import MultiDocumentAgentEnabledCell from '@features/admin-multi-document-agent/components/EnabledCell';
import { Typography } from '@mui/material';
import { MultiDocumentAgentTemplateRow } from '@app/types/admin';
import { MultiDocumentAgentTableMeta } from '@app/types/overrides/table-meta';
import {
  MultiDocumentAgentTemplateStatus,
  MultiDocumentAgentTemplateType,
} from '@utils/enums.ts';
import { updateDepositionSummaryTemplate } from '@api/deposition-summary-template';
import { updateUseCaseTemplate } from '@api/use-case-template';

const columnHelper = createColumnHelper<MultiDocumentAgentTemplateRow>();

const createMultiDocumentAgentTemplateColumns = (
  reloadRecords: () => void,
  navigate: NavigateFunction
) => {
  const getUpdateFunction = (templateType: MultiDocumentAgentTemplateType) => {
    if (templateType === MultiDocumentAgentTemplateType.DEMAND_LETTER) {
      return updateDocumentGeneratorTemplate;
    }
    if (templateType === MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY) {
      return updateDepositionSummaryTemplate;
    }
    if (templateType === MultiDocumentAgentTemplateType.USE_CASE) {
      return updateUseCaseTemplate;
    }
    return undefined as any;
  };

  const handleClickCell = (original: MultiDocumentAgentTemplateRow) => {
    if (
      original.template_type === MultiDocumentAgentTemplateType.DEMAND_LETTER
    ) {
      navigate(
        routes.superAdmin.multiDocumentAgent.demandLetter.edit(original.id)
      );
    } else if (
      original.template_type ===
      MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY
    ) {
      navigate(
        routes.superAdmin.multiDocumentAgent.depositionSummary.edit(original.id)
      );
    } else if (
      original.template_type === MultiDocumentAgentTemplateType.USE_CASE
    ) {
      navigate(routes.superAdmin.multiDocumentAgent.useCase.edit(original.id));
    }
  };

  return [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Name',
      enableSorting: true,
      size: 200,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <TableUpdateNameCell
          id={original.id}
          value={original.name}
          onSuccess={reloadRecords}
          onUpdate={getUpdateFunction(original.template_type)}
          onClickCell={() => handleClickCell(original)}
        />
      ),
    }),
    columnHelper.accessor('is_enabled', {
      id: 'is_enabled',
      header: 'Enabled',
      enableSorting: true,
      size: 20,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <MultiDocumentAgentEnabledCell enabled={original.is_enabled} />
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      enableSorting: true,
      size: 100,
      enableResizing: false,
      cell: ({ row: { original } }) => <StatusTag status={original.status} />,
    }),
    columnHelper.accessor('template_type', {
      id: 'template_type',
      header: 'Type',
      size: 200,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">{original.template_type}</Typography>
      ),
    }),
    columnHelper.accessor('use_case_group_name', {
      id: 'use_case_group_name',
      header: 'Category',
      size: 150,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">
          {original.use_case_group_name || '-'}
        </Typography>
      ),
    }),
    columnHelper.display({
      id: 'id',
      header: 'Quick Actions',
      enableSorting: false,
      enableResizing: false,
      size: 20,
      cell: ({
        row: { original },
        table: {
          options: { meta },
        },
      }) => {
        const tableMeta = meta as MultiDocumentAgentTableMeta<typeof original>;
        const canMoveUpAndDown = [
          MultiDocumentAgentTemplateType.DEMAND_LETTER,
          MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY,
          MultiDocumentAgentTemplateType.USE_CASE,
        ].includes(original.template_type);
        const canDuplicate = [
          MultiDocumentAgentTemplateType.DEMAND_LETTER,
          MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY,
          MultiDocumentAgentTemplateType.USE_CASE,
        ].includes(original.template_type);

        return (
          <MainTableRowActions>
            {original.status !== MultiDocumentAgentTemplateStatus.PENDING && (
              <>
                <AnimateButton>
                  <Tooltip title="Edit Document">
                    <IconButton
                      onClick={() => {
                        tableMeta?.handleEditRow &&
                          tableMeta?.handleEditRow(original);
                      }}
                    >
                      <IconPencil />
                    </IconButton>
                  </Tooltip>
                </AnimateButton>
                {canMoveUpAndDown && (
                  <>
                    <AnimateButton>
                      <Tooltip title="Move Up">
                        <IconButton
                          size="small"
                          onClick={() => {
                            tableMeta?.handleMoveUp &&
                              tableMeta?.handleMoveUp(original);
                          }}
                        >
                          <IconArrowNarrowUp />
                        </IconButton>
                      </Tooltip>
                    </AnimateButton>
                    <AnimateButton>
                      <Tooltip title="Move Down">
                        <IconButton
                          size="small"
                          onClick={() => {
                            tableMeta?.handleMoveDown &&
                              tableMeta?.handleMoveDown(original);
                          }}
                        >
                          <IconArrowNarrowDown />
                        </IconButton>
                      </Tooltip>
                    </AnimateButton>
                  </>
                )}
                <AnimateButton>
                  <Tooltip title="Delete Document">
                    <IconButton
                      onClick={() => {
                        tableMeta?.handleDeleteRow &&
                          tableMeta?.handleDeleteRow(original);
                      }}
                    >
                      <IconTrash />
                    </IconButton>
                  </Tooltip>
                </AnimateButton>

                {canDuplicate && (
                  <AnimateButton>
                    <Tooltip title="Duplicate Template">
                      <IconButton
                        onClick={() => {
                          tableMeta?.handleDuplicateRow &&
                            tableMeta?.handleDuplicateRow(original);
                        }}
                      >
                        <IconCopy />
                      </IconButton>
                    </Tooltip>
                  </AnimateButton>
                )}
              </>
            )}
          </MainTableRowActions>
        );
      },
    }),
  ];
};

export default createMultiDocumentAgentTemplateColumns;
